import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiCheckCircle, FiXCircle, FiLoader, FiEye, FiEyeOff } from 'react-icons/fi';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);
// --- Helper: Debounce Function (no changes) ---
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

// --- API Helper Object (no changes) ---
const API_BASE_URL = 'https://ethiccraft.org/backend/';
const api = {
  sendEmailOtp: async (email) => {
    const response = await fetch(`${API_BASE_URL}sign_up/send_otp.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send OTP');
    }
    return data;
  },
  verifyEmailOtp: async (email, otp) => {
    const response = await fetch(`${API_BASE_URL}sign_up/verify_otp.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp })
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'OTP verification failed');
    }
    return data;
  },
  checkUsername: async (username, signal) => {
    const response = await fetch(`${API_BASE_URL}sign_up/check_username_availability.php?username=${encodeURIComponent(username)}`, { signal });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Server returned a non-JSON error.' }));
      throw new Error(errorData.message || `Network response was not ok. Status: ${response.status}`);
    }
    return response.json();
  },
  checkEmail: async (email, signal) => {
    const response = await fetch(`${API_BASE_URL}sign_up/check_email_availability.php?email=${encodeURIComponent(email)}`, { signal });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to check email');
    }
    return data;
  },
  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}sign_up/create.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Registration failed.');
    }
    return data;
  },
};


const RegistrationForm = () => {
  const navigate = useNavigate();

  // --- State Management ---
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phoneNumber: "",
    password: "",
    otp: "",
  });

  const [agreed, setAgreed] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [loading, setLoading] = useState({
    username: false,
    email: false,
    otpSend: false,
    otpVerify: false,
    submit: false,
  });

  // OTP and availability status
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isUsernameOk, setIsUsernameOk] = useState(null);
  const [isEmailOk, setIsEmailOk] = useState(null);

  // Resend OTP Timer
  const [resendCooldown, setResendCooldown] = useState(0);
  const timerRef = useRef(null);


  // --- Field Validation ---
  const validateField = useCallback((name, value) => {
    switch (name) {
      case "username":
        if (!value) return "Username is required.";
        if (!/^[a-zA-Z0-9_]{6,15}$/.test(value))
          return "Must be 6-15 characters (letters, numbers, underscore).";
        return "";
      case "email":
        if (!value) return "Email is required.";
        if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value))
          return "Invalid email format.";
        return "";
      case "phoneNumber":
        if (!value) return "Phone number is required.";
        if (!/^\d{10}$/.test(value)) return "Phone number must be 10 digits.";
        return "";
      case "password":
        if (!value) return "Password is required.";
        if (value.length < 8) return "Password must be at least 8 characters.";
        if (!/(?=.*[a-z])/.test(value)) return "Must contain a lowercase letter.";
        if (!/(?=.*[A-Z])/.test(value)) return "Must contain an uppercase letter.";
        if (!/(?=.*\d)/.test(value)) return "Must contain a number.";
        if (!/(?=.*[@$!%*?&_#-])/.test(value)) return "Must contain a special character.";
        return "";
      default:
        return "";
    }
  }, []);

  // --- Input Change & Blur Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));

    if (name === "username") setIsUsernameOk(null);
    if (name === "email") {
      setIsEmailOk(null);
      setOtpSent(false);
      setOtpVerified(false);
      setFormData(prev => ({ ...prev, otp: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };


  // --- Debounced API Checks with AbortController for Race Condition ---
  const checkUsername = useCallback(debounce(async (username, signal) => {
    if (validateField("username", username)) {
      setLoading(prev => ({ ...prev, username: false }));
      return;
    }
    try {
      const data = await api.checkUsername(username, signal);
      if (signal.aborted) return;
      if (data.available) {
        setIsUsernameOk(true);
        setFormErrors(prev => ({ ...prev, username: '' }));
      } else {
        setIsUsernameOk(false);
        setFormErrors(prev => ({ ...prev, username: data.message || "Username is already taken." }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setIsUsernameOk(false);
        setFormErrors(prev => ({ ...prev, username: error.message || "Could not verify username." }));
      }
    } finally {
      setLoading(prev => ({ ...prev, username: false }));
    }
  }, 500), [validateField]);

  const checkEmail = useCallback(debounce(async (email, signal) => {
    if (validateField("email", email)) {
      setLoading(prev => ({ ...prev, email: false }));
      return;
    }
    try {
      const data = await api.checkEmail(email, signal);
      if (signal.aborted) return;
      if (data.available) {
        setIsEmailOk(true);
        setFormErrors(prev => ({ ...prev, email: '' }));
      } else {
        setIsEmailOk(false);
        setFormErrors(prev => ({ ...prev, email: data.message || "Email is already registered." }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setIsEmailOk(false);
        setFormErrors(prev => ({ ...prev, email: error.message || "Could not verify email." }));
      }
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  }, 500), [validateField]);

  // --- useEffect for Live Validation ---
  useEffect(() => {
    const controller = new AbortController();
    if (formData.username) {
      setLoading(prev => ({ ...prev, username: true }));
      checkUsername(formData.username, controller.signal);
    }
    return () => controller.abort();
  }, [formData.username, checkUsername]);

  useEffect(() => {
    const controller = new AbortController();
    if (formData.email) {
      setLoading(prev => ({ ...prev, email: true }));
      checkEmail(formData.email, controller.signal);
    }
    return () => controller.abort();
  }, [formData.email, checkEmail]);

  // --- Cooldown Timer Effect ---
  useEffect(() => {
    if (resendCooldown > 0) {
      timerRef.current = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [resendCooldown]);


  // --- Event Handlers ---
  const handleSendOtp = async (isResend = false) => {
    setLoading(prev => ({ ...prev, otpSend: true }));
    setFormErrors(prev => ({ ...prev, otp: '' }));
    try {
      await api.sendEmailOtp(formData.email);
      setOtpSent(true);
      setResendCooldown(30); // Start 30s cooldown
      if (isResend) {
        // You can add a success toast here e.g. "OTP Resent!"
      }
    } catch (error) {
      setFormErrors(prev => ({ ...prev, otp: error.message || "Failed to send OTP." }));
    } finally {
      setLoading(prev => ({ ...prev, otpSend: false }));
    }
  };

  const handleVerifyOtp = async () => {
    if (!formData.otp || formData.otp.length < 6) {
      setFormErrors(prev => ({ ...prev, otp: 'Please enter a valid 6-digit OTP.' }));
      return;
    }
    setLoading(prev => ({ ...prev, otpVerify: true }));
    setFormErrors(prev => ({ ...prev, otp: '' }));
    try {
      await api.verifyEmailOtp(formData.email, formData.otp);
      setOtpVerified(true);
      clearTimeout(timerRef.current); // Stop timer on success
    } catch (error) {
      setFormErrors(prev => ({ ...prev, otp: error.message || "OTP is incorrect." }));
      setOtpVerified(false);
    } finally {
      setLoading(prev => ({ ...prev, otpVerify: false }));
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();

    // --- Final Validation Check on all fields ---
    const finalErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) finalErrors[key] = error;
    });
    setFormErrors(prev => ({ ...prev, ...finalErrors }));
    setTouched({ username: true, email: true, phoneNumber: true, password: true });

    if (Object.keys(finalErrors).length > 0 || isSubmitDisabled) {
      setFormErrors(prev => ({ ...prev, submit: "Please fix the errors before submitting." }));
      return;
    }

    setLoading(prev => ({ ...prev, submit: true }));
    setFormErrors(prev => ({ ...prev, submit: null }));

    const userData = {
      username: formData.username,
      email: formData.email,
      phone_number: formData.phoneNumber,
      password: formData.password
    };

    try {
      await api.register(userData);
      await MySwal.fire({
        icon: 'success',
        title: 'Registration Successful!',
        text: 'You have been successfully registered. Redirecting to login...',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Go to Login',
      });
      navigate('/ProfileEdit');
    } catch (error) {
      setFormErrors(prev => ({ ...prev, submit: error.message || "An unexpected error occurred." }));
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  // --- Memoized Values for Readability & Performance ---
  const isFormValid = useMemo(() => {
    return Object.values(formErrors).every(error => !error);
  }, [formErrors]);

  const isSubmitDisabled = useMemo(() => {
    return !isFormValid || !isUsernameOk || !isEmailOk || !otpVerified || !agreed || Object.values(loading).some(state => state);
  }, [isFormValid, isUsernameOk, isEmailOk, otpVerified, agreed, loading]);

  const passwordStrength = useMemo(() => {
    const value = formData.password;
    let score = 0;
    if (value.length >= 8) score++;
    if (/(?=.*[a-z])/.test(value) && /(?=.*[A-Z])/.test(value)) score++;
    if (/(?=.*\d)/.test(value)) score++;
    if (/(?=.*[@$!%*?&_#-])/.test(value)) score++;
    return score;
  }, [formData.password]);

  // --- Render Helpers ---
  const renderStatusIcon = (status, isLoading) => {
    if (isLoading) return <FiLoader className="loading-icon" />;
    if (status === true) return <FiCheckCircle className="status-icon success-icon" />;
    if (status === false) return <FiXCircle className="status-icon error-icon" />;
    return null;
  };

  return (
    <div className="form-page-container">
      <div className="form-container-card">
        <div className="form-image-section">
          {/* You can place an image here using <img /> or a background-image */}
          <div className="form-image-overlay">
            <h3>Welcome to EthicCraft</h3>
            <p>Discover a community of integrity and passion. Let's get you started on your journey.</p>
          </div>
        </div>

        <div className="form-section">
          <h1 className="form-title">Create Account</h1>
          <p className="form-subtitle">Join us and start your ethical journey today.</p>

          <form onSubmit={handleRegistrationSubmit} noValidate>

            {/* --- Username --- */}
            <div className={`input-group ${formData.username ? 'has-value' : ''}`}>
              <input
                id="username" name="username" type="text"
                className={`input-field ${touched.username && formErrors.username ? 'input-error' : ''}`}
                value={formData.username}
                onChange={handleInputChange} onBlur={handleBlur}
                disabled={loading.submit}
              />
              <label htmlFor="username">Username</label>
              <div className="input-status-icon">{renderStatusIcon(isUsernameOk, loading.username)}</div>
            </div>
            {touched.username && formErrors.username && <small className="error-message">{formErrors.username}</small>}

            {/* --- Email & OTP --- */}
            <div className="email-otp-group">
              <div className={`input-group ${formData.email ? 'has-value' : ''}`}>
                <input
                  id="email" name="email" type="email"
                  className={`input-field ${touched.email && formErrors.email ? 'input-error' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange} onBlur={handleBlur}
                  disabled={otpSent || loading.submit}
                />
                <label htmlFor="email">Email Address</label>
                <div className="input-status-icon">{renderStatusIcon(isEmailOk, loading.email)}</div>
              </div>
              {!otpSent && (
                <button type="button" className="action-btn" onClick={() => handleSendOtp(false)}
                  disabled={!isEmailOk || loading.email || loading.otpSend}>
                  {loading.otpSend ? <FiLoader className="loading-icon" /> : "Send OTP"}
                </button>
              )}
            </div>
            {touched.email && formErrors.email && <small className="error-message">{formErrors.email}</small>}

            {/* --- OTP Verification Section --- */}
            {otpSent && !otpVerified && (
              <div className="otp-section">
                <div className="info-text">
                  <p>An OTP has been sent to <strong>{formData.email}</strong>.</p>
                  <button type="button" className="change-email-btn" onClick={() => setOtpSent(false)}>Change Email</button>
                </div>
                <div className="otp-input-group">
                  <div className={`input-group ${formData.otp ? 'has-value' : ''}`}>
                    <input
                      id="otp" name="otp" type="text" maxLength="6"
                      className={`input-field ${formErrors.otp ? 'input-error' : ''}`}
                      value={formData.otp}
                      onChange={handleInputChange}
                      disabled={loading.otpVerify || otpVerified}
                    />
                    <label htmlFor="otp">Enter 6-Digit OTP</label>
                  </div>
                  <button type="button" className="action-btn verify-btn" onClick={handleVerifyOtp}
                    disabled={!formData.otp || loading.otpVerify || otpVerified}>
                    {loading.otpVerify ? <FiLoader className="loading-icon" /> : "Verify"}
                  </button>
                </div>
                {formErrors.otp && <small className="error-message">{formErrors.otp}</small>}
                <div className="resend-otp-container">
                  <button type="button" onClick={() => handleSendOtp(true)} disabled={resendCooldown > 0 || loading.otpSend}>
                    {loading.otpSend ? "Sending..." : "Resend OTP"}
                  </button>
                  {resendCooldown > 0 && <span className="cooldown-timer">(Resend available in {resendCooldown}s)</span>}
                </div>
              </div>
            )}
            {otpVerified && <p className="success-message-box">✅ Email successfully verified!</p>}

            {/* --- Phone Number --- */}
            <div className={`input-group ${formData.phoneNumber ? 'has-value' : ''}`}>
              <input
                id="phoneNumber" name="phoneNumber" type="tel"
                className={`input-field ${touched.phoneNumber && formErrors.phoneNumber ? 'input-error' : ''}`}
                value={formData.phoneNumber}
                onChange={handleInputChange} onBlur={handleBlur}
                disabled={loading.submit}
              />
              <label htmlFor="phoneNumber">10-Digit Phone Number</label>
            </div>
            {touched.phoneNumber && formErrors.phoneNumber && <small className="error-message">{formErrors.phoneNumber}</small>}

            {/* --- Password --- */}
            <div className={`input-group ${formData.password ? 'has-value' : ''}`}>
              <input
                id="password" name="password" type={passwordVisible ? "text" : "password"}
                className={`input-field ${touched.password && formErrors.password ? 'input-error' : ''}`}
                value={formData.password}
                onChange={handleInputChange} onBlur={handleBlur}
                disabled={loading.submit}
              />
              <label htmlFor="password">Password</label>
              <button type="button" className="password-toggle-btn" onClick={() => setPasswordVisible(!passwordVisible)}>
                {passwordVisible ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {touched.password && formErrors.password && <small className="error-message">{formErrors.password}</small>}

            {/* --- Password Strength Meter --- */}
            {formData.password && (
              <div className="password-strength-meter">
                <div className={`strength-bar strength-${passwordStrength}`}></div>
              </div>
            )}

            {/* --- Agreement Checkbox --- */}
            <div className="checkbox-group">
              <input
                type="checkbox" id="agreeTerms"
                checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                disabled={loading.submit}
              />
              <label htmlFor="agreeTerms" className="checkbox-label">
                <span className="custom-checkbox"></span>
                I agree to become a member of EthicCraft.
              </label>
            </div>

            {/* --- Final Submit Error Display --- */}
            {formErrors.submit && <p className="submit-error-message">{formErrors.submit}</p>}

            {/* --- Submit Button --- */}
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitDisabled}>
              {loading.submit ? <><FiLoader className="loading-icon" />Creating Account...</> : "Submit Registration"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;